import { Table, Anchor, Button, Modal, Loader, Center, Text, Input, Group, UnstyledButton, Select } from '@mantine/core';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchProposalList, deleteProposal } from '../../services/proposalsRoutes';
import { IconX, IconEdit, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { ProposalListItem } from '../../interfaces/types';
import { useTranslation } from 'react-i18next';
import useLocalStorage from '@/hooks/useLocalStorage';

type SortField = 'title' | 'last_edited' | 'client' | 'letterSenders' | 'displayId';
type SortDirection = 'asc' | 'desc';
type FilterField = 'all' | 'title' | 'client' | 'letterSenders' | 'displayId';

// A component that generates a table row when given a row from proposalData
function ProposalRow({ row, onDelete }: { row: ProposalListItem; onDelete: () => void }) {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const proposalId = row.id;
  const lastEditDate =
    row.last_edited instanceof Date
      ? row.last_edited
      : row.last_edited
        ? new Date(row.last_edited)
        : new Date("2024-01-01");
  const formattedDate =
    lastEditDate instanceof Date ? lastEditDate.toISOString().split('T')[0] : '';

  const handleEditClick = () => {
    const newPath = `${location.pathname}/edit-proposal`;
    navigate(newPath, { state: { proposalId: proposalId } });
  };

  const handleDeleteClick = () => {
    async function deleteProposalData() {
      const accessToken = await getAccessTokenSilently();
      await deleteProposal(accessToken, proposalId);
      onDelete(); // Call the callback function after deletion
    }
    deleteProposalData();
  };

  const formatLetterSenders = (senders: string[]) => {
    if (!senders || senders.length === 0) return '-';
    if (senders.length === 1) return senders[0];
    if (senders.length === 2) return senders.join(', ');
    return `${senders[0]}, ${senders[1]} +${senders.length - 2}`;
  };

  return (
    <Table.Tr key={row.title}>
      <Table.Td style={{ maxWidth: '300px' }}>
        <Anchor component="button" fz="sm" onClick={handleEditClick} style={{ 
          display: 'block',
          wordBreak: 'break-word',
          lineHeight: '1.4'
        }}>
          {row.title}
        </Anchor>
      </Table.Td>
      <Table.Td style={{ minWidth: '120px' }}>{row.client || '-'}</Table.Td>
      <Table.Td style={{ minWidth: '120px' }}>{formatLetterSenders(row.letterSenders)}</Table.Td>
      <Table.Td style={{ minWidth: '120px' }}>{row.displayId || t('idUnavailable')}</Table.Td>
      <Table.Td style={{ minWidth: '90px', whiteSpace: 'nowrap' }}>{formattedDate}</Table.Td>
      <Table.Td style={{ width: '80px', textAlign: 'right' }}>
        <Group gap={4} justify="flex-end" wrap="nowrap">
        <Button
          variant="light"
          color="green"
            size="compact-xs"
          onClick={handleEditClick}
        >
            <IconEdit size={12} />
        </Button>

        <Button
          variant="light"
          color="red"
            size="compact-xs"
          onClick={() => setIsModalOpen(true)}
        >
            <IconX size={12} />
        </Button>
        </Group>
        <Modal
          opened={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={t('confirmDeletion')}
        >
          <div>{t('confirmDeleteProposal')}</div>
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', gap: '12px' }}
          >
            <Button color="gray" onClick={() => setIsModalOpen(false)}>
              {t('cancel')}
            </Button>
            <Button color="red" onClick={handleDeleteClick}>
              {t('confirm')}
            </Button>
          </div>
        </Modal>
      </Table.Td>
    </Table.Tr>
  );
}

export default function ProposalTable() {
  const [proposalData, setProposalData] = useState<ProposalListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useLocalStorage<string>('proposals.searchTerm', '');
  const [filterField, setFilterField] = useLocalStorage<FilterField>('proposals.filterField', 'all');
  const [sortField, setSortField] = useLocalStorage<SortField>('proposals.sortField', 'last_edited');
  const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>('proposals.sortDirection', 'desc');
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
    console.log('Fetching data');
    const accessToken = await getAccessTokenSilently();
    console.log('Token received');
    const fetchedData = await fetchProposalList(accessToken);
      const modifiedData = fetchedData?.map((item: ProposalListItem) => {
      return {
        ...item,
        last_edited: item.last_edited ? new Date(item.last_edited) : undefined,
      };
    });
    setProposalData(modifiedData);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'last_edited' ? 'desc' : 'asc'); // Default to desc for dates, asc for text
    }
  };

  const filteredAndSortedData = useMemo(() => {
    // First filter the data
    let filtered = proposalData;
    
    if (searchTerm) {
      filtered = proposalData.filter((proposal) => {
        const searchLower = searchTerm.toLowerCase();
        
        switch (filterField) {
          case 'title':
            return proposal.title.toLowerCase().includes(searchLower);
          case 'client':
            return proposal.client?.toLowerCase().includes(searchLower);
          case 'letterSenders':
            return proposal.letterSenders?.some(sender => 
              sender.toLowerCase().includes(searchLower)
            );
          case 'displayId':
            return proposal.displayId?.toLowerCase().includes(searchLower);
          case 'all':
          default:
            return (
              proposal.title.toLowerCase().includes(searchLower) ||
              proposal.client?.toLowerCase().includes(searchLower) ||
              proposal.letterSenders?.some(sender => 
                sender.toLowerCase().includes(searchLower)
              ) ||
              proposal.displayId?.toLowerCase().includes(searchLower)
            );
        }
      });
    }

    // Then sort the filtered data
    return filtered.sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      if (sortField === 'last_edited') {
        aValue = a.last_edited || new Date(0);
        bValue = b.last_edited || new Date(0);
        
        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      } else {
        // For text fields, use Polish locale-aware comparison
        let aText: string;
        let bText: string;
        
        if (sortField === 'client') {
          aText = a.client || '';
          bText = b.client || '';
        } else if (sortField === 'letterSenders') {
          aText = a.letterSenders?.join(', ') || '';
          bText = b.letterSenders?.join(', ') || '';
        } else if (sortField === 'displayId') {
          aText = a.displayId || '';
          bText = b.displayId || '';
        } else {
          aText = a.title;
          bText = b.title;
        }
        
        const comparison = aText.localeCompare(bText, 'pl', { 
          sensitivity: 'base',
          numeric: true 
        });
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });
  }, [proposalData, searchTerm, filterField, sortField, sortDirection]);

  const SortableHeader = ({ field, children, width }: { field: SortField; children: React.ReactNode; width?: string }) => {
    const isActive = sortField === field;
    const icon = isActive ? (
      sortDirection === 'asc' ? (
        <IconChevronUp size={14} />
      ) : (
        <IconChevronDown size={14} />
      )
    ) : null;

    return (
      <Table.Th style={{ width }}>
        <UnstyledButton onClick={() => handleSort(field)} style={{ width: '100%' }}>
          <Group gap={4} justify="space-between">
            <Text fw={500}>{children}</Text>
            {icon}
          </Group>
        </UnstyledButton>
      </Table.Th>
    );
  };

  if (loading) {
    return (
      <Center style={{ height: '20vh' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <div style={{ width: '100%', padding: '0 0.5rem' }}>
      {/* Search and Filter Controls */}
      <Group mb="xl" gap="md" justify="flex-start" wrap="nowrap">
        <Input
          placeholder={t('searchProposals')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: '400px' }}
        />
        <Select
          placeholder={t('filterBy')}
          value={filterField}
          onChange={(value) => setFilterField(value as FilterField)}
          data={[
            { value: 'all', label: t('allFields') },
            { value: 'title', label: t('title') },
            { value: 'client', label: t('client') },
            { value: 'letterSenders', label: t('letterSenders') },
            { value: 'displayId', label: t('displayId') },
          ]}
          style={{ minWidth: '160px' }}
        />
      </Group>

      <Table.ScrollContainer minWidth={820} w="100%">
        <Table verticalSpacing="md" horizontalSpacing="sm" striped>
        <Table.Thead>
          <Table.Tr>
              <SortableHeader field="title" width="auto">{t('proposal')}</SortableHeader>
              <SortableHeader field="client" width="120px">{t('client')}</SortableHeader>
              <SortableHeader field="letterSenders" width="120px">{t('letterSenders')}</SortableHeader>
              <SortableHeader field="displayId" width="120px">{t('displayId')}</SortableHeader>
              <SortableHeader field="last_edited" width="90px">{t('dateEdited')}</SortableHeader>
              <Table.Th style={{ width: '80px', textAlign: 'center' }}>{t('actions')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
            {filteredAndSortedData.length > 0 ? (
              filteredAndSortedData.map((row) => <ProposalRow key={row.id} row={row} onDelete={fetchData} />)
            ) : (
              <Table.Tr>
                <Table.Td colSpan={6} style={{ textAlign: 'center', padding: '3rem' }}>
                  <Text c="dimmed" size="lg">
                    {searchTerm ? t('noSearchResults') : t('noProposalsFound')}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
    </div>
  );
}
